'use client';

import { useState, useSyncExternalStore } from 'react';
import Image from 'next/image';

const DISMISS_KEY = 'tw_ios_banner_dismissed';
const SUPPRESSION_MS = 30 * 24 * 60 * 60 * 1000;

const APP_LIVE = process.env.NEXT_PUBLIC_IOS_APP_LIVE === 'true';
const APP_STORE_URL = 'https://apps.apple.com/app/id6761959582';
const PRESS_URL = '/press';

function shouldShowBanner(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
  if (!isIOS) return false;

  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  // Safari's native smart banner (via apple-itunes-app meta) covers the live case.
  // Only skip when we're actually emitting the native banner (i.e. app is live).
  if (isSafari && APP_LIVE) return false;

  try {
    const dismissedAt = window.localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const ts = parseInt(dismissedAt, 10);
      if (!Number.isNaN(ts) && Date.now() - ts < SUPPRESSION_MS) return false;
    }
  } catch {
    // localStorage unavailable (private mode etc.) — show banner anyway.
  }

  return true;
}

const subscribeNoop = () => () => {};

export default function IOSPromoBanner() {
  const canShow = useSyncExternalStore(subscribeNoop, shouldShowBanner, () => false);
  const [dismissed, setDismissed] = useState(false);
  const visible = canShow && !dismissed;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {
      // Ignore.
    }
    setDismissed(true);
  };

  if (!visible) return null;

  const ctaLabel = APP_LIVE ? 'Get' : 'Coming soon';
  const ctaHref = APP_LIVE ? APP_STORE_URL : PRESS_URL;

  return (
    <div
      role="region"
      aria-label="iOS app promotion"
      className="fixed top-0 left-0 right-0 z-[100] bg-white/95 dark:bg-neutral-900/95 backdrop-blur border-b border-neutral-200 dark:border-neutral-800 transition-opacity duration-300"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss iOS app banner"
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <Image
          src="/favicon/apple-touch-icon-180x180.png"
          alt=""
          width={36}
          height={36}
          className="flex-shrink-0 rounded-lg"
          unoptimized
        />

        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 leading-tight truncate">
            TimeyWimey
          </div>
          <div className="text-xs text-neutral-600 dark:text-neutral-400 leading-tight truncate">
            Every time zone, on one dial.
          </div>
        </div>

        <a
          href={ctaHref}
          className="flex-shrink-0 px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          {ctaLabel}
        </a>
      </div>
    </div>
  );
}
